import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  Lock,
  ShieldCheck,
  Wrench,
  PenLine,
  FileText,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { paymentsApi } from "../lib/api";
import SignaturePad from "../components/SignaturePad";
import { PoweredBy } from "../components/PoweredBy";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AcceptEstimate() {
  const { estimateId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const suggestedDeposit = parseFloat(params.get("deposit") || "0");

  const [est, setEst] = useState(null); // null=loading | obj | false=error
  const [errorMsg, setErrorMsg] = useState("");
  const [step, setStep] = useState("review"); // review | deposit | done | converted
  const [polling, setPolling] = useState(!!sessionId);
  const pollAttempts = useRef(0);

  // signing
  const padRef = useRef(null);
  const [signerName, setSignerName] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const [signing, setSigning] = useState(false);

  // deposit
  const [depAmount, setDepAmount] = useState("");
  const [startingDep, setStartingDep] = useState(false);

  const applyEstimate = (e) => {
    setEst(e);
    if (!signerName) setSignerName(e.customer || "");
    if (e.status === "Converted") {
      setStep("converted");
    } else if (e.signed) {
      setStep(e.depositDue > 0 && e.onlinePaymentEnabled ? "deposit" : "done");
    } else {
      setStep("review");
    }
    if (!depAmount) {
      const def = suggestedDeposit > 0 ? suggestedDeposit : Math.round(e.total * 0.25 * 100) / 100;
      setDepAmount(String(Math.min(def, e.depositDue)));
    }
  };

  const load = useCallback(async () => {
    try {
      const e = await paymentsApi.getEstimate(estimateId);
      if (!sessionId) applyEstimate(e);
      else setEst(e); // polling flow sets step after status
    } catch (err) {
      setEst(false);
      setErrorMsg(err?.response?.data?.detail || "We couldn't find this estimate.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, sessionId]);

  const poll = useCallback(async () => {
    const MAX = 8;
    try {
      const res = await paymentsApi.status(sessionId);
      if (res.payment_status === "paid") {
        setPolling(false);
        setStep("done");
        if (res.estimate) setEst((p) => ({ ...(p || {}), ...res.estimate }));
        return;
      }
      if (res.status === "expired") {
        setPolling(false);
        setStep("deposit");
        toast.error("Payment session expired — you can try again.");
        return;
      }
    } catch (_) {
      /* keep trying */
    }
    pollAttempts.current += 1;
    if (pollAttempts.current >= MAX) {
      setPolling(false);
      setStep("deposit");
      return;
    }
    setTimeout(poll, 2000);
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (sessionId) {
      pollAttempts.current = 0;
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const sign = async () => {
    if (padRef.current?.isEmpty()) {
      toast.error("Please sign before accepting");
      return;
    }
    if (!signerName.trim()) {
      toast.error("Enter your name");
      return;
    }
    setSigning(true);
    try {
      const dataUrl = padRef.current.toDataURL();
      const updated = await paymentsApi.acceptEstimate(estimateId, { dataUrl, signedBy: signerName.trim() });
      setEst(updated);
      toast.success("Estimate accepted");
      setStep(updated.depositDue > 0 && updated.onlinePaymentEnabled ? "deposit" : "done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept the estimate");
    } finally {
      setSigning(false);
    }
  };

  const payDeposit = async () => {
    const amt = parseFloat(depAmount);
    if (!amt || amt < (est.minPayment || 0.5)) {
      toast.error(`Minimum deposit is ${money(est.minPayment || 0.5)}`);
      return;
    }
    setStartingDep(true);
    try {
      const res = await paymentsApi.createDepositCheckout(estimateId, window.location.origin, Math.min(amt, est.depositDue));
      window.location.href = res.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the deposit");
      setStartingDep(false);
    }
  };

  const accent = est && est.company?.accentColor ? est.company.accentColor : "#2563EB";
  const logoUrl =
    est && est.company?.logoDataUrl
      ? est.company.logoDataUrl.startsWith("http")
        ? est.company.logoDataUrl
        : `${BACKEND}${est.company.logoDataUrl}`
      : null;

  return (
    <div className="min-h-screen bg-[#0B1E3F] flex items-center justify-center p-4 py-10" data-testid="accept-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-2.5 mb-5">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-9 max-w-[160px] object-contain" />
          ) : (
            <>
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center">
                <Wrench className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="text-white font-bold text-[17px] tracking-tight">{est?.company?.name || "ServicePro"}</div>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {est === null && (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mb-2" />
              <div className="text-[13px]">Loading your estimate…</div>
            </div>
          )}

          {est === false && (
            <div className="py-16 px-6 text-center" data-testid="accept-error">
              <div className="h-12 w-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-6 w-6 text-rose-500" />
              </div>
              <div className="text-[15px] font-semibold text-slate-900">Estimate unavailable</div>
              <div className="text-[13px] text-slate-500 mt-1">{errorMsg}</div>
            </div>
          )}

          {est && est !== false && (
            <>
              {/* Estimate header + line items (always visible except while polling) */}
              {!polling && (
                <div className="px-6 pt-6 pb-4 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                        Estimate {est.id}
                      </div>
                      <div className="text-[15px] font-semibold text-slate-900 mt-0.5">{est.customer}</div>
                      {est.title && <div className="text-[12.5px] text-slate-500">{est.title}</div>}
                    </div>
                    <span
                      className={`text-[11px] font-bold px-2 py-1 rounded-md ${
                        est.status === "Accepted" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {est.status}
                    </span>
                  </div>
                  <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden">
                    {(est.lineItems || []).map((li, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-[12.5px] border-b border-slate-100 last:border-0">
                        <span className="text-slate-700 truncate pr-3">
                          {li.description} {li.qty > 1 ? <span className="text-slate-400">× {li.qty}</span> : null}
                        </span>
                        <span className="text-slate-900 font-medium shrink-0">{money(li.total)}</span>
                      </div>
                    ))}
                    <div className="bg-slate-50 px-3 py-2 space-y-0.5 text-[12px]">
                      <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{money(est.subtotal)}</span></div>
                      {est.discount > 0 && <div className="flex justify-between text-rose-600"><span>Discount</span><span>−{money(est.discount)}</span></div>}
                      <div className="flex justify-between text-slate-500"><span>Tax ({est.taxRate || 0}%)</span><span>{money(est.taxAmount)}</span></div>
                      <div className="flex justify-between pt-1 border-t border-slate-200 text-[14px] font-bold text-slate-900"><span>Total</span><span>{money(est.total)}</span></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Polling */}
              {polling && (
                <div className="py-20 flex flex-col items-center justify-center text-slate-500" data-testid="accept-polling">
                  <Loader2 className="h-7 w-7 animate-spin mb-3" style={{ color: accent }} />
                  <div className="text-[14px] font-semibold text-slate-800">Confirming your deposit…</div>
                </div>
              )}

              {/* Step: review + sign */}
              {!polling && step === "review" && (
                <div className="px-6 py-5 space-y-3" data-testid="accept-sign-step">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
                    <PenLine className="h-4 w-4" style={{ color: accent }} /> Sign to accept this estimate
                  </div>
                  {est.signoffText && (
                    <div
                      data-testid="accept-signoff-paragraph"
                      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-wrap"
                      style={{ maxHeight: "8rem", overflowY: "auto" }}
                    >
                      {est.signoffText}
                    </div>
                  )}
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Your name</label>
                    <input
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      data-testid="accept-signer-name"
                      placeholder="Full name"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />
                  </div>
                  <SignaturePad ref={padRef} onChange={setHasInk} />
                  <div className="text-[11px] text-slate-500">
                    By signing, you accept the estimate total of <strong>{money(est.total)}</strong>.
                  </div>
                  <Button
                    onClick={sign}
                    disabled={signing || !hasInk}
                    data-testid="accept-sign-btn"
                    className="w-full h-12 text-white text-[14px] font-bold rounded-lg disabled:opacity-60"
                    style={{ background: accent }}
                  >
                    {signing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Accept &amp; sign
                  </Button>
                </div>
              )}

              {/* Step: deposit */}
              {!polling && step === "deposit" && (
                <div className="px-6 py-5 space-y-3" data-testid="accept-deposit-step">
                  <div className="flex items-center gap-2 text-emerald-700 text-[13px] font-semibold">
                    <CheckCircle2 className="h-4 w-4" /> Accepted — thank you{est.signedBy ? `, ${est.signedBy}` : ""}!
                  </div>
                  <div className="text-[13px] text-slate-600">
                    Would you like to pay a deposit now to get scheduled faster?
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                      Deposit amount (max {money(est.depositDue)})
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[14px]">$</span>
                      <input
                        type="number"
                        min={est.minPayment || 0.5}
                        max={est.depositDue}
                        step="0.01"
                        value={depAmount}
                        onChange={(e) => setDepAmount(e.target.value)}
                        data-testid="accept-deposit-amount"
                        className="w-full h-11 pl-7 pr-3 rounded-lg border border-slate-200 text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                      />
                    </div>
                    <div className="mt-2 flex items-center gap-1.5">
                      {[0.25, 0.5, 1].map((p) => (
                        <button
                          key={p}
                          onClick={() => setDepAmount(String(Math.min(est.depositDue, Math.round(est.total * p * 100) / 100)))}
                          className="px-2 py-1 rounded-md border border-slate-200 text-[11px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700"
                        >
                          {p === 1 ? "Full" : `${p * 100}%`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={payDeposit}
                    disabled={startingDep}
                    data-testid="accept-pay-deposit-btn"
                    className="w-full h-12 text-white text-[14px] font-bold rounded-lg"
                    style={{ background: accent }}
                  >
                    {startingDep ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                    Pay deposit {money(Math.min(parseFloat(depAmount || 0), est.depositDue))}
                  </Button>
                  <button
                    onClick={() => setStep("done")}
                    data-testid="accept-skip-deposit"
                    className="w-full text-center text-[12.5px] text-slate-500 hover:text-slate-700 font-medium py-1"
                  >
                    Skip — I'll pay later
                  </button>
                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
                    <Lock className="h-3 w-3" /> Secure payment powered by Stripe
                  </div>
                </div>
              )}

              {/* Step: done */}
              {!polling && step === "done" && (
                <div className="py-12 px-6 text-center" data-testid="accept-done">
                  <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                  <div className="text-[18px] font-bold text-slate-900">You're all set!</div>
                  <div className="text-[13px] text-slate-500 mt-1">
                    Estimate <strong>{est.id}</strong> has been accepted{est.depositPaid > 0 ? ` and a deposit of ${money(est.depositPaid)} was received` : ""}.
                    We'll be in touch to schedule your work.
                  </div>
                  {est.depositDue > 0 && est.depositPaid > 0 && (
                    <div className="mt-4 text-[12px] text-slate-400">Remaining balance due at service: {money(est.depositDue)}</div>
                  )}
                </div>
              )}

              {/* Converted */}
              {!polling && step === "converted" && (
                <div className="py-12 px-6 text-center" data-testid="accept-converted">
                  <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
                    <FileText className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="text-[16px] font-bold text-slate-900">This estimate has been invoiced</div>
                  <div className="text-[13px] text-slate-500 mt-1">No further action is needed here.</div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-4">
          <ShieldCheck className="h-3.5 w-3.5" /> Signed &amp; paid securely. Your details are protected.
        </div>
        <PoweredBy dark className="mt-3" />
      </div>
    </div>
  );
}
